import React, { useState, useEffect } from 'react';
import { useBilling } from '../hooks/useBilling';
import { useAuth } from '../hooks/useAuth';
import { useAppContext } from '../hooks/useAppContext';
import { formatPrice, formatUsd, hasAvailableCredit, safeParseNumber } from '../types';
import JobsTable from './JobsTable';
import BillingHistory from './BillingHistory';
import PaymentMethodModal from './PaymentMethodModal';
import './AccountPage.css';

const AccountPage: React.FC = () => {
  const { apiKey } = useAuth();
  const { state } = useAppContext();
  const {
    balance,
    getMonthlyUsage
  } = useBilling();
  
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [activeTab, setActiveTab] = useState<'jobs' | 'transactions'>('jobs');
  const [monthlyUsage, setMonthlyUsage] = useState<{ amount: number; contacts: number }>({ amount: 0, contacts: 0 });

  useEffect(() => {
    const loadMonthlyUsage = async () => {
      try {

        
        // Use the new getMonthlyUsage function from the billing hook
        const usageData = await getMonthlyUsage();
        
        if (usageData) {

          setMonthlyUsage(usageData);
        } else {

        }
      } catch (error) {
        console.error('❌ Error loading monthly usage:', error);
      }
    };

    if (apiKey) {
      loadMonthlyUsage();
    }
  }, [apiKey, balance, getMonthlyUsage]);

  const needsPaymentMethod = !balance?.has_payment_method;
  const availableCredit = safeParseNumber(balance?.balance);
  const showCredit = hasAvailableCredit(availableCredit);
  const pricePerContact = safeParseNumber(
    balance?.custom_price_per_contact || state.schema?.ui_config?.billing?.price_per_contact || 0.02
  );
  const contactsCovered = pricePerContact > 0 ? Math.floor(availableCredit / pricePerContact) : 0;

  return (
    <div className="account-page">
      {/* Account Overview Section */}
      <div className="account-overview">
        {showCredit && (
          <div className="overview-card">
            <div className="card-content">
              <h3>AVAILABLE CREDIT</h3>
              <div className="usage-amount">{formatUsd(availableCredit)}</div>
              <div className="usage-contacts">
                {contactsCovered.toLocaleString()} contacts remaining
              </div>
            </div>
          </div>
        )}

        <div className="overview-card payment-card">
          <div className="card-content">
            <h3>PAYMENT METHOD</h3>
            <div className="card-status">
              {needsPaymentMethod ? (
                <>
                  <span className="status-inactive">Inactive</span>
                  <button 
                    className="update-button"
                    onClick={() => setShowPaymentMethod(true)}
                  >
                    Set Payment Method
                  </button>
                </>
              ) : (
                <>
                  <span className="status-active">Active</span>
                  <button 
                    className="update-button"
                    onClick={() => setShowPaymentMethod(true)}
                  >
                    Update
                  </button>
                </>
              )}
            </div>
            <div className="pricing-info">
              {balance?.custom_price_per_contact ? (
                <>
                  <span style={{ fontWeight: 600, color: '#2563eb' }}>Custom pricing: ${formatPrice(balance.custom_price_per_contact)} per contact</span>
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>(Standard: ${formatPrice(state.schema?.ui_config?.billing?.price_per_contact || '0.02')})</span>
                </>
              ) : (
                `Pay-as-you-go pricing: $${formatPrice(state.schema?.ui_config?.billing?.price_per_contact || '0.02')} per contact`
              )}
            </div>
          </div>
        </div>

        <div className="overview-card usage-card">
          <div className="card-content">
            <h3>USAGE THIS MONTH</h3>
            <div className="usage-amount">${monthlyUsage.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="usage-contacts">
              {monthlyUsage.contacts.toLocaleString()} contacts
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Interface */}
      <div className="account-tabs">
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            Job History
          </button>
          <button 
            className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            Transaction History
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'jobs' && (
            <div className="jobs-tab">
              <JobsTable 
                isAdminMode={false}
                showUserColumn={false}
                emptyStateTitle="No Jobs Found"
                emptyStateDescription="Your job history will appear here once you start running operations"
                maxHeight="600px"
              />
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="transactions-tab">
              <BillingHistory />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPaymentMethod && (
        <PaymentMethodModal
          onClose={() => setShowPaymentMethod(false)}
        />
      )}
    </div>
  );
};

export default AccountPage; 